import React, {useEffect, useRef} from "react";
import MarkerClusterGroup from "react-leaflet-markercluster";
import {ResultCard} from "./ResultCards/ResultCard";
import {Marker, Popup, useLeaflet} from "react-leaflet";
import {divIcon, Point} from "leaflet";
import {renderToString} from "react-dom/server";
import "leaflet.markercluster/dist/leaflet.markercluster";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import styled from "styled-components";
import {rgba} from "polished";
import {GetColorFromUnitType, GetTextColor} from "Helpers/ColorHelper";
import {FlexContainer} from "BaseComponents/Flex";
import {getLatLangsFromMarkersData} from "Helpers/MapHelper";
import {useGlobal} from "reactn";
import {Paragraph} from "BaseComponents/Text";
import useBreakpoint from "Hooks/useBreakpoint";

export const SingleMarker = (color) => {
    return divIcon({
        html: renderToString(
            <SingleMarkerIcon color={color} className="icon-location-1"/>
        ),
        className: "",
        iconSize: [25, 41],
        iconAnchor: [10, 41],
        popupAnchor: [2, -40],
    });
};
const iconCreateFunction = (cluster) => {
    return divIcon({
        html: renderToString(<MarkerClusterIcon count={cluster.getChildCount()}/>),
        className: "",
        iconSize: new Point(40, 40),
    });
};

const calculateSize = (count, isOuter) => {
    let size = isOuter ? 10 + count * 4 : 5 + count * 4;
    return !isOuter ? size : size * 1.3;
};
const MarkerClusterIcon = ({count}) => {
    const [theme] = useGlobal("theme");

    return (
        <IconOuter theme={theme} size={calculateSize(count, true)}>
            <IconInner
                textColor={GetTextColor(theme.colors.secondary).textColor}
                theme={theme}
                count={count}
                size={calculateSize(count)}
            >
                <span>{count}</span>
            </IconInner>
        </IconOuter>
    );
};

export const MarkerCluster = ({
                                  markers,
                                  DirectionsLinkText,
                                  selectedValue,
                                  nearestMarkers,
                                  YouAreHereText = "",
                              }) => {
    const groupRef = useRef(null);
    const currentMarker = useRef(null);
    const isMobile = useBreakpoint("mobile");

    const [theme] = useGlobal("theme");

    const {map} = useLeaflet();
    let HasSelectedValue =
        selectedValue != undefined && selectedValue.length != 0;
    map.once("moveend", function (e) {
        if (currentMarker.current != undefined && selectedValue != undefined) {
            setTimeout(() => {
                currentMarker.current.leafletElement.openPopup();
            }, 300);
        }
        () => clearTimeout();
    });
    
    map.once("popupopen", function (e) {
        if(e.popup) {
            const closeButton = e.popup._container.querySelector('.close--Button');
            if (closeButton) {
                closeButton.focus();
            }
        }
    });

    useEffect(() => {
        const group = groupRef.current.leafletElement;
        if (nearestMarkers != undefined && nearestMarkers.length) {

            let markers = getLatLangsFromMarkersData(nearestMarkers);
            markers.push(selectedValue);

            markers = markers.filter(c => c !== undefined && c !== '');
            if (!!markers.length) {
                map.fitBounds(markers, {maxZoom: 15});
            }
        } else if (group.getBounds() != undefined) {
            if (group.getBounds().isValid()) map.fitBounds(group.getBounds());
        }
    }, [markers, nearestMarkers]);
    
    const closePopup = () => {
        map.closePopup();
    }

    return (
        <MarkerClusterGroup ref={groupRef} iconCreateFunction={iconCreateFunction}>
            {markers.map((marker, key) => {
                const {Position: position, UnitType, Company, GlobalUnit, ...rest} = marker;
                if (position == null || !position?.Latitude?.length && !position?.Longitude.length) {
                    return;
                }
                const positionData = {
                    lat: position?.Latitude,
                    lng: position?.Longitude,
                };

                const color =
                    GetColorFromUnitType(UnitType, theme, Company, GlobalUnit) ?? theme.colors.primary;
                return (
                    <Marker
                        key={`cluster-marker-${key}`}
                        position={positionData}
                        icon={SingleMarker(color)}
                        onClick={(e) => {
                            if (isMobile) {
                                const lngToPoint = map.latLngToLayerPoint(e.latlng);
                                const pointToLng = map.layerPointToLatLng({x: lngToPoint.x, y: (lngToPoint.y - 200)});
                                map.panTo(pointToLng);
                            }
                        }}
                    >
                        <Popup keepInView={false} autoPanPadding={new Point(45,85)} className={'test '}>
                            <ResultCard
                                isPopup={true}
                                DirectionsLinkText={DirectionsLinkText}
                                marker={{...rest, ...{UnitType, Company, GlobalUnit}}}
                                directionLink={`https://www.google.com/maps/dir/?api=1&destination=${position.Latitude},${position.Longitude}`}
                                onPopupClose={closePopup}
                            />
                        </Popup>
                    </Marker>
                );
            })}
            {HasSelectedValue && (
                <Marker
                    ref={currentMarker}
                    position={selectedValue}
                    icon={SingleMarker("black")}
                >
                    <Popup>
                        <FlexContainer
                            padding="10px 0"
                            width="100px"
                            smallwidth="80px"
                            alignitems="center"
                            smallAlignContent="center"
                            justifyContent="center"
                        >
                            <Paragraph>{YouAreHereText}</Paragraph>
                        </FlexContainer>
                    </Popup>
                </Marker>
            )}
        </MarkerClusterGroup>
    );
};

const IconOuter = styled.div`
    width: ${({size}) => `${size}px`};
    height: ${({size}) => `${size}px`};
    background: ${({theme}) => rgba(theme.colors.secondary, 0.5)};

    color: white;
    border-radius: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 35px;
    min-height: 35px;
    max-height: 60px;
    max-width: 60px;
`;
export const SingleMarkerIcon = styled.i`
    margin: 0;
    color: ${({color}) => color};
    font-size: 40px;

    &:before {
        margin: 0;
        width: 0;
    }
`;

const IconInner = styled.div`
    width: ${({size}) => `${size}px`};
    height: ${({size}) => `${size}px`};
    background: ${({theme}) => theme.colors.secondary};
    color: white;
    border-radius: 100px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 22px;
    min-width: 22px;
    max-height: 40px;
    max-width: 40px;

    span {
        color: ${({textColor}) => textColor};
        font-weight: bold;
        font-size: ${({count}) =>
                count < 3 ? "14px" : count > 3 && count < 7 ? "16px" : "18px"};
    }
`;
